import type {
  FederalPovertyLevel,
  HouseholdIncome,
} from '@hc-frontend/core-types';
import { QualifyingLifeEvent } from '@hc-frontend/core-types';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import type { Nullable } from '@hc-frontend/core-utils-types';
import { v1 } from '@hc-frontend/services-lead-storage';

import {
  type GetProspectRepository,
  LeadStatus,
  type Prospect,
} from '../../domain';
import { contactMapper } from './contact-mapper';
import { journeyAttributesMapper } from './journey-attributes-mapper';
import { locationMapper } from './location-mapper';
import { memberMapper } from './member-mapper';
import { userAttributesMapper } from './user-attributes-mapper';

const leadStatusMap = {
  [LeadStatus.Enum.FRESH2]: v1.GetExternalLeadResponseStatus.FRESH2,
  [LeadStatus.Enum.PURCHASE_COMPLETED]:
    v1.GetExternalLeadResponseStatus.PURCHASE_COMPLETED,
  [LeadStatus.Enum.PROSPECT]: v1.GetExternalLeadResponseStatus.PROSPECT,
  [LeadStatus.Enum.SUBMITTED]: v1.GetExternalLeadResponseStatus.SUBMITTED,
  [LeadStatus.Enum.ENRICHED]: v1.GetExternalLeadResponseStatus.ENRICHED,
  [LeadStatus.Enum.SCORED]: v1.GetExternalLeadResponseStatus.SCORED,
  [LeadStatus.Enum.DELIVERED]: v1.GetExternalLeadResponseStatus.DELIVERED,
  [LeadStatus.Enum.ROUTED]: v1.GetExternalLeadResponseStatus.ROUTED,
};

const qualifyingLifeEventAnswersMap = {
  [v1.QualifyingLifeEvent.Enum['lost-coverage']]:
    QualifyingLifeEvent.Enum.LostCoverage,
  [v1.QualifyingLifeEvent.Enum['got-new-job']]:
    QualifyingLifeEvent.Enum.IncomeChanged,
  [v1.QualifyingLifeEvent.Enum['had-a-baby']]: QualifyingLifeEvent.Enum.Baby,
  [v1.QualifyingLifeEvent.Enum['moved-new-state']]:
    QualifyingLifeEvent.Enum.Moved,
  [v1.QualifyingLifeEvent.Enum['none-of-these']]:
    QualifyingLifeEvent.Enum.Other,
};

export function isConsideredSoldBasedOnStatus(
  status: Nullable<v1.GetExternalLeadResponseStatus>,
) {
  return (
    status === v1.GetExternalLeadResponseStatus.SUBMITTED ||
    status === v1.GetExternalLeadResponseStatus.ENRICHED ||
    status === v1.GetExternalLeadResponseStatus.SCORED ||
    status === v1.GetExternalLeadResponseStatus.ROUTED ||
    status === v1.GetExternalLeadResponseStatus.DELIVERED
  );
}

export function getProspectAdapterFactory({
  baseUrl,
}: {
  baseUrl: string;
}): GetProspectRepository {
  return {
    getProspect: async (params) => {
      if (!params.sessionId && !params.userId) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToGetProspect',
            },
          ],
        });
      }

      const leadStatusId = params.leadStatus
        ? leadStatusMap[params.leadStatus].toString()
        : undefined;

      const response = await v1.getExternalLeadService({ baseUrl })({
        ...params,
        leadStatusId,
      });

      if (response.status !== 200 || response.body === undefined) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToGetProspect',
              rootCause: new Error(
                `Session ${params.sessionId} could not be retreived`,
              ),
            },
          ],
        });
      }

      const { userAttributes, journeyAttributes } = response.body;

      const userAttributesMap = userAttributesMapper(userAttributes);

      const journeyAttributesMap = journeyAttributesMapper(journeyAttributes);

      const { applicant, spouse, children } = memberMapper(
        userAttributes,
        userAttributesMap,
      );

      let qualifyingLifeEvent;
      if (journeyAttributesMap['quality-life-event']) {
        qualifyingLifeEvent =
          qualifyingLifeEventAnswersMap[
            journeyAttributesMap['quality-life-event']
          ];
      }

      const leadSession: Prospect = {
        location: locationMapper(userAttributesMap),
        householdSize: userAttributesMap.householdSize || 0,
        householdIncome: userAttributesMap.householdIncome as HouseholdIncome,
        federalPovertyLevel: userAttributesMap.fpl as FederalPovertyLevel,
        qualifyingLifeEvent,
        applicant,
        spouse,
        children,
        contactInformation: contactMapper(userAttributesMap),
        leadId: response.body.tcpaUniversalId
          ? response.body.tcpaUniversalId
          : undefined,
        sold: isConsideredSoldBasedOnStatus(response.body.leadStatusId),
      };

      return createSuccessOperationResult(leadSession);
    },
  };
}
